import { Component, OnInit, ViewChild } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { MatSelect } from '@angular/material/select';
import { Router, ActivatedRoute } from '@angular/router';
import { NotifierService } from 'angular-notifier';
import { BoobleModalService } from 'booble-modal';
import { ReplaySubject, Subject, take, takeUntil } from 'rxjs';
import { AddressDto } from 'src/app/domain/models/dto/address.dto';
import { ContactDto } from 'src/app/domain/models/dto/contact.dto';
import { CountryDto } from 'src/app/domain/models/dto/country.dto';
import { IndividualAccountDto } from 'src/app/domain/models/dto/individual-account.dto';
import { AuthenticationService } from 'src/app/shared/services';
import { AddressService } from 'src/app/shared/services/address.service';
import { IndividualAccountService } from 'src/app/shared/services/individual-account.service';
import { RegisterService } from 'src/app/shared/services/register.service';
import { countries } from 'src/app/shared/utils/countries';
import { CepResponse } from 'src/app/domain/models/interfaces/cep-response.interface';

@Component({
  selector: 'app-individual-data',
  templateUrl: './individual-data.component.html',
  styleUrls: ['./individual-data.component.scss'],
})
export class IndividualDataComponent implements OnInit {
  private readonly notifier: NotifierService;
  userLogin: string;
  individualId: number;
  tenant: string = '';
  countries: CountryDto[] = countries;
  public loading: boolean;
  public loadingCep: boolean;
  private lastSearchedCep: string = '';
  individualForm: FormGroup;
  startDate = new Date();
  public countryListFilterCtrl = new FormControl('');
  public filteredCountryList: ReplaySubject<CountryDto[]> = new ReplaySubject<
    CountryDto[]
  >(1);

  @ViewChild('singleSelect', { static: true }) singleSelect: MatSelect;

  protected _onDestroy = new Subject<void>();

  get individualFormControl() {
    return this.individualForm.controls;
  }

  get individualAccount(): IndividualAccountDto {
    const individual = this.individualForm.getRawValue();
    individual.contact = new ContactDto(
      individual.phone,
      individual.email,
      new AddressDto(
        individual.line1,
        individual.line2,
        individual.line3,
        individual.isResidentAbroad,
        individual.complement,
        individual.number,
        individual.postalCode,
        individual.country,
        individual.city,
        individual.state
      )
    );
    individual.address = individual.contact.address;
    return individual;
  }

  set individualAccount(individual: IndividualAccountDto) {
    if (individual) {
      individual.id = Number(this.individualId);
      this.individualForm.patchValue(individual);
      if (individual.contact) {
        this.individualForm.controls['line1'].setValue(
          individual.contact?.address?.line1
        );
        this.individualForm.controls['line3'].setValue(
          individual.contact?.address?.line3
        );
        this.individualForm.controls['number'].setValue(
          individual.contact?.address?.number ?? ''
        );
        this.individualForm.controls['postalCode'].setValue(
          individual.contact?.address?.postalCode ?? ''
        );
        this.individualForm.controls['country'].setValue(
          individual.contact?.address?.country ?? ''
        );
        this.individualForm.controls['city'].setValue(
          individual.contact?.address?.city ?? ''
        );
        this.individualForm.controls['state'].setValue(
          individual.contact?.address?.state ?? ''
        );
        this.individualForm.controls['isResidentAbroad'].setValue(
          individual.contact?.address?.isResidentAbroad
        );
        this.individualForm.controls['complement'].setValue(
          individual.contact?.address?.complement ?? ''
        );
      }
    }
  }

  constructor(
    fb: FormBuilder,
    notifierService: NotifierService,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly registerService: RegisterService,
    private readonly authService: AuthenticationService,
    private readonly addressService: AddressService,
    private readonly individualService: IndividualAccountService,
    private readonly modalService: BoobleModalService
  ) {
    this.notifier = notifierService;
    this.userLogin = this.authService.getCurrentUserId();
    this.individualId = +this.authService.getIndividualId();

    this.individualForm = fb.group({
      id: [],
      name: ['', [Validators.required, Validators.pattern('.*\\S.*')]],
      cpf: ['', [Validators.required, Validators.pattern('.*\\S.*')]],
      birthday: ['', [Validators.required, Validators.pattern('.*\\S.*')]],
      nacionality: ['', [Validators.required, Validators.pattern('.*\\S.*')]],
      sex: ['', [Validators.required, Validators.pattern('.*\\S.*')]],
      isPublicAgent: [
        false,
        [Validators.required, Validators.pattern('.*\\S.*')],
      ],
      publicAgentInformation: [''],
      annualBilling: ['', [Validators.required, Validators.min(1)]],
      isResidentAbroad: [
        false,
        [Validators.required, Validators.pattern('.*\\S.*')],
      ],
      line1: ['', [Validators.required, Validators.pattern('.*\\S.*')]],
      city: [''],
      line3: [''],
      state: [''],
      complement: [''],
      postalCode: [''],
      number: [''],
      country: [''],
    });
  }

  ngOnInit() {
    this.route.paramMap.subscribe((params) => {
      const result = params.get('tenant');

      if (result) this.tenant = params.get('tenant');
    });

    this.filteredCountryList.next(this.countries);

    this.countryListFilterCtrl.valueChanges
      .pipe(takeUntil(this._onDestroy))
      .subscribe(() => {
        this.filterCountryList();
      });

    this.individualService.get(this.individualId).subscribe((getResult) => {
      this.individualAccount = getResult.data;
      this.individualForm.controls['name'].disable();
      this.individualForm.controls['cpf'].disable();
      this.individualForm.controls['birthday'].disable();
      this.individualForm.controls['nacionality'].disable();

      this.requiredAbroad(
        this.individualForm,
        this.individualForm.value.isResidentAbroad
      );
    });
  }

  ngOnDestroy() {
    this._onDestroy.next();
    this._onDestroy.complete();
  }

  protected setInitialValue() {
    this.filteredCountryList
      .pipe(take(1), takeUntil(this._onDestroy))
      .subscribe(() => {
        this.singleSelect.compareWith = (a: CountryDto, b: CountryDto) =>
          a && b && a.nome === b.nome;
      });
  }

  protected filterCountryList() {
    if (!this.countries) {
      return;
    }

    let search = this.countryListFilterCtrl.value;

    if (!search) {
      this.filteredCountryList.next(this.countries);
      return;
    } else {
      this.filteredCountryList.next(
        this.countries.filter((company) => {
          return company.nome.toLowerCase().includes(search.toLowerCase());
        })
      );
    }
  }

  changeCountry(event: any) {
    this.individualForm.controls['country'].setValue(event.value);
  }

  setAddress(form: FormGroup) {
    if (this.loadingCep) return;
    const postalCode: string = form.getRawValue().postalCode?.replace(/\D/g, '') || '';
    if (postalCode.length === 8 && postalCode !== this.lastSearchedCep) {
      this.lastSearchedCep = postalCode;
      this.loadingCep = true;
      this.addressService.getCep(postalCode).subscribe((res: CepResponse) => {
        if (res.error) {
          this.notifier.notify(
            'warning',
            'Não foi possível buscar o CEP. Preencha o endereço manualmente.'
          );
        } else {
          if (res.street) {
            form.controls['line1'].setValue(res.street);
          }
          if (res.neighborhood) {
            form.controls['line3'].setValue(res.neighborhood);
          }
          if (res.city) {
            form.controls['city'].setValue(res.city);
          }
          if (res.state) {
            form.controls['state'].setValue(res.state);
          }
        }
        this.loadingCep = false;
      });
    }
  }

  requiredJob(form: FormGroup, value: boolean) {
    if (value && form.contains('publicAgentInformation')) {
      form.controls['publicAgentInformation'].setValidators([
        Validators.required,
        Validators.pattern('.*\\S.*'),
      ]);
    } else if (!value && form.contains('publicAgentInformation')) {
      form.controls['publicAgentInformation'].clearValidators();
      form.controls['publicAgentInformation'].setErrors(null);
      form.controls['publicAgentInformation'].updateValueAndValidity();
      form.controls['publicAgentInformation'].setValue('');
    }
  }

  requiredAbroad(form: FormGroup, isResidentAbroad: boolean) {
    if (isResidentAbroad && form.contains('country')) {
      form.controls['country'].setValidators([
        Validators.required,
        Validators.pattern('.*\\S.*'),
      ]);
      form.controls['city'].clearValidators();
      form.controls['city'].setValue('');
      form.controls['line3'].clearValidators();
      form.controls['line3'].setValue('');
      form.controls['complement'].clearValidators();
      form.controls['complement'].setValue('');
      form.controls['state'].clearValidators();
      form.controls['state'].setValue('');
      form.controls['postalCode'].clearValidators();
      form.controls['postalCode'].setValue('');
      form.controls['number'].clearValidators();
      form.controls['number'].setValue('');
    } else if (!isResidentAbroad && form.contains('country')) {
      form.controls['country'].clearValidators();
      form.controls['country'].setErrors(null);
      form.controls['country'].setValue('Brazil');
      form.controls['country'].updateValueAndValidity();
      form.controls['city'].setValidators([
        Validators.required,
        Validators.pattern('.*\\S.*'),
      ]);
      form.controls['line3'].setValidators([
        Validators.required,
        Validators.pattern('.*\\S.*'),
      ]);
      form.controls['state'].setValidators([
        Validators.required,
        Validators.pattern('.*\\S.*'),
      ]);
      form.controls['postalCode'].setValidators([
        Validators.required,
        Validators.pattern('.*\\S.*'),
      ]);
      form.controls['number'].setValidators([
        Validators.required,
        Validators.pattern('.*\\S.*'),
      ]);
    }
  }

  update() {
    if (this.individualForm.valid) {
      this.loading = true;

      this.individualService
        .putRegister(this.individualAccount, Number(this.individualId))
        .subscribe({
          next: () => {
            this.router.navigate([`${this.tenant}/onboardings/fatca-pep`]);
          },
          error: (err) => {
            this.loading = false;
          },
        });
    }
  }
}
